import React, { createContext, useState, useEffect } from 'react';
import {
  ExposedContextData,
  Newsletter,
  GetNewslettersResponse,
  Privilege,
} from '../types/types';
import {
  getNewslettersForAA,
  getPrivilegeCheck,
} from '../services/signed_in.api.service';
import {
  getEmailSetting,
  updateEmailSetting,
} from '../../services/email_settings/email_settings.api.service';
import { MOCKED_NEWSLETTERS } from '../constants/mocked_data';
import { OPT_OUT_SETTING } from '../constants/constants';
import { GENERIC_ERROR_MESSAGE } from '../../components/react/alert_popup/constants';

interface Response {
  receives_partner_emails?: boolean;
  opt_out_of_all_emails?: boolean;
}

interface Props {
  children: React.ReactNode;
}

const SignedInContext = createContext<ExposedContextData>(null);

const SignedInContextProvider: React.FC<Props> = ({ children }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOptedOutOfAllEmails, setIsOptedOutOfAllEmails] = useState<boolean>(false);
  const [isOptedOutOnFirstMount, setIsOptedOutOnFirstMount] = useState<boolean>(false);
  const [newsletters, setNewsletters] = useState<Newsletter[]>(MOCKED_NEWSLETTERS);
  const [deniedPrivileges, setDeniedPrivileges] = useState<string[]>([]);
  const [alertData, setAlertData] = useState(null);

  // On component mount, performs a request to check if user has opted out
  // of all emails or not
  useEffect((): void => {
    if (isLoading) return;
    setIsLoading(true);
    getEmailSetting('opted_out_of_all_emails')
      .then((response: boolean) => {
        setIsOptedOutOfAllEmails(response);
        setIsOptedOutOnFirstMount(response);
      })
      .catch(() => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, }))
      .finally(() => setIsLoading(false));
  }, []);

  /* On component mount, fetch the list of newsletters and filter them by category:
   *   - 'general'
   *   - 'focus_areas'
   */
  useEffect((): void => {
    if (isLoading) return;
    setIsLoading(true);
    getNewslettersForAA()
      .then((response: GetNewslettersResponse) => {
        setNewsletters(
          response.data.filter((newsletter) =>
            ['general', 'focus_areas'].includes(newsletter.category)
          )
        );
      })
      .catch((): void => {
        setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, });
        setNewsletters([]);
      })
      .finally((): void => setIsLoading(false));
  }, []);

  /* When the newsletter list is loaded, for the newsletters which contain
   * a required privilege, check if current user has such privilege to subscribe / un-subscribe
   * to those newsletters
   */
  useEffect((): void => {
    if (!newsletters.length) return;
    setIsLoading(true);

    const privilegesToBeChecked = newsletters
      .filter((newsletter) => newsletter?.required_privilege?.constant_name)
      .map((newsletter) => newsletter.required_privilege.constant_name);

    Promise.all(privilegesToBeChecked.map((privilege) => getPrivilegeCheck(privilege)))
      .then((checkedPrivileges: Privilege[]) => {
        setDeniedPrivileges(
          checkedPrivileges
            .filter((privilege) => !privilege.allowed)
            .map((privilege) => privilege.privilege)
        );
      })
      .catch((): void => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, }))
      .finally((): void => setIsLoading(false));
  }, [newsletters.length]);

  const toggleOptedOutOfAllEmails = (): Promise<Response|void> =>
    updateEmailSetting(OPT_OUT_SETTING, !isOptedOutOfAllEmails)
      .then((response) => {
        setIsOptedOutOfAllEmails(response[OPT_OUT_SETTING]);

        if (!response[OPT_OUT_SETTING]) {
          setAlertData({
            type: 'success',
            message: 'Well done! You successfully opted into Devex mailing.',
          });
        } else {
          setAlertData({
            type: 'warning',
            message: 'Warning! You opted out of all Devex mailings, including newsletters.',
          });
        }
      })
      .catch((): void => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, }))
      .finally((): void => setIsLoading(false));

  const exposedContextData = {
    isLoading,
    isOptedOutOfAllEmails,
    isOptedOutOnFirstMount,
    toggleOptedOutOfAllEmails,
    newsletters,
    setNewsletters,
    deniedPrivileges,
    // The following variables are only relevant for the UAContext. Define and hardcode them.
    // The UI is resilient enough to manage this.
    preselectedNewsletters: [],
    userEmail: '',
    setUserEmail: (): null => null,
    setPreselectedNewsletters: (): null => null,
    isUAFormSubmitted: false,
    setIsUAFormSubmitted: (): null => null,
    emailFromUrl: null,
    alertData,
    setAlertData,
  };

  return (
    <SignedInContext.Provider value={exposedContextData}>
      {children}
    </SignedInContext.Provider>
  );
};

export { SignedInContext, SignedInContextProvider };
